/**
 * BWOF job row/status colors (app-wide).
 *
 * Inspection result rule (after visit / outcome):
 *   Pass (COMPLETED) → green | Incomplete (IN_PROGRESS, …) → yellow | Fail → red
 *
 * Scheduling / due colours stay separate (pending red, scheduled blue, overdue purple, …).
 */
import {
  inspectionResultBadgeClass,
  normalizeInspectionResult,
  INSPECTION_RESULT,
} from './inspectionResultColor';

/**
 * @param {string} status - job.status
 * @param {number | null | undefined} daysUntilDue
 * @param {boolean} [isBuildingDue]
 */
export function getJobRowStyle(status, daysUntilDue, isBuildingDue = false) {
  const effectiveStatus = status || (isBuildingDue ? 'Due soon' : null);

  if (effectiveStatus === 'OVERDUE') {
    return {
      row: 'bg-purple-100 border-l-4 border-purple-600',
      date: 'text-purple-900 font-semibold',
      badge: 'bg-purple-800 text-white',
    };
  }

  if (effectiveStatus === 'PENDING' || effectiveStatus === 'Due soon') {
    const highlightDate =
      daysUntilDue != null && (daysUntilDue < 0 || (daysUntilDue >= 0 && daysUntilDue <= 30));
    return {
      row: 'bg-red-100 border-l-4 border-red-500',
      date: highlightDate ? 'text-red-800 font-semibold' : 'text-gray-900 font-medium',
      badge: 'bg-red-200 text-red-900',
    };
  }

  if (['QUERIED', 'AUCTION'].includes(effectiveStatus)) {
    return {
      row: 'bg-yellow-100 border-l-4 border-yellow-500',
      date: 'text-yellow-900 font-medium',
      badge: 'bg-yellow-200 text-yellow-900',
    };
  }

  if (['ACCEPTED', 'SCHEDULED'].includes(effectiveStatus)) {
    return {
      row: 'bg-blue-100 border-l-4 border-blue-500',
      date: 'text-blue-800 font-medium',
      badge: 'bg-blue-200 text-blue-900',
    };
  }

  // Incomplete inspection / work in progress → yellow
  if (
    effectiveStatus === 'IN_PROGRESS' ||
    effectiveStatus === 'REPAIRING' ||
    effectiveStatus === 'AFTER_PAPERWORK'
  ) {
    return {
      row: 'bg-yellow-100 border-l-4 border-yellow-500',
      date: 'text-yellow-900 font-medium',
      badge: inspectionResultBadgeClass(INSPECTION_RESULT.INCOMPLETE),
    };
  }

  if (effectiveStatus === 'NOTIFIED') {
    return {
      row: 'bg-violet-50 border-l-4 border-violet-500',
      date: 'text-violet-900 font-medium',
      badge: 'bg-violet-200 text-violet-900',
    };
  }

  // Pass / completed → green
  if (effectiveStatus === 'COMPLETED') {
    return {
      row: 'bg-green-50 border-l-4 border-green-500',
      date: 'text-green-800 font-medium',
      badge: inspectionResultBadgeClass(INSPECTION_RESULT.PASS),
    };
  }

  if (['FAILED', 'REJECTED'].includes(effectiveStatus)) {
    return {
      row: 'bg-red-50 border-l-4 border-red-500',
      date: 'text-red-800 font-medium',
      badge: inspectionResultBadgeClass(INSPECTION_RESULT.FAIL),
    };
  }

  if (effectiveStatus === 'WAITING') {
    return {
      row: 'bg-yellow-100 border-l-4 border-yellow-500',
      date: 'text-yellow-900 font-medium',
      badge: 'bg-yellow-200 text-yellow-900',
    };
  }

  const result = normalizeInspectionResult(effectiveStatus);
  if (result === INSPECTION_RESULT.PASS) {
    return {
      row: 'bg-green-50 border-l-4 border-green-500',
      date: 'text-green-800 font-medium',
      badge: inspectionResultBadgeClass(result),
    };
  }
  if (result === INSPECTION_RESULT.FAIL) {
    return {
      row: 'bg-red-50 border-l-4 border-red-500',
      date: 'text-red-800 font-medium',
      badge: inspectionResultBadgeClass(result),
    };
  }
  if (result === INSPECTION_RESULT.INCOMPLETE) {
    return {
      row: 'bg-yellow-100 border-l-4 border-yellow-500',
      date: 'text-yellow-900 font-medium',
      badge: inspectionResultBadgeClass(result),
    };
  }

  return {
    row: '',
    date: 'text-gray-900',
    badge: 'bg-gray-100 text-gray-800',
  };
}

/** Mail log only: not send mail, sent mail, or read mail. */
export function mailProgressLabel(job) {
  const facts = Array.isArray(job?.notify_facts) ? job.notify_facts : [];
  const read = job?.schedule_mail_state === 'read' || facts.some((fact) => fact?.opened_at);
  const sent =
    read ||
    job?.schedule_mail_state === 'sent' ||
    facts.some((fact) => Number(fact?.send_count) > 0);
  if (read) return 'read mail';
  if (sent) return 'sent mail';
  return 'not send mail';
}

/** Real mail progress while the inspection is still at the mail step. */
export function mailListLabel(job) {
  const status = String(job?.status || '').toUpperCase();
  if (status !== 'NOTIFIED') return JOB_STATUS_LABELS[job?.status] || job?.status || '—';
  return mailProgressLabel(job);
}

export const JOB_STATUS_LABELS = {
  PENDING: 'Issued',
  QUERIED: 'Awaiting reply',
  AUCTION: 'Auction',
  ACCEPTED: 'Accepted',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  REPAIRING: 'Repairing',
  NOTIFIED: 'Mail',
  AFTER_PAPERWORK: 'After paperwork',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  WAITING: 'Waiting',
  'Due soon': 'Due soon',
};

export function getStatusFilterOptionClass(filterValue) {
  if (!filterValue || filterValue === 'ALL') return 'text-gray-700';
  if (filterValue === 'OVERDUE' || filterValue === 'DUE_WORK') return 'text-purple-800 font-medium';
  if (filterValue === 'PENDING') return 'text-red-800 font-medium';
  if (filterValue === 'QUERIED,AUCTION' || filterValue === 'QUERIED' || filterValue === 'AUCTION') {
    return 'text-yellow-800 font-medium';
  }
  if (filterValue === 'ACCEPTED' || filterValue === 'SCHEDULED') return 'text-blue-800 font-medium';
  if (
    filterValue === 'IN_PROGRESS' ||
    filterValue === 'REPAIRING' ||
    filterValue === 'AFTER_PAPERWORK'
  ) {
    return 'text-yellow-800 font-medium';
  }
  if (filterValue === 'NOTIFIED') return 'text-violet-800 font-medium';
  if (filterValue === 'COMPLETED') return 'text-green-800 font-medium';
  if (filterValue === 'FAILED' || filterValue === 'REJECTED') return 'text-red-800 font-medium';
  if (filterValue === 'WAITING') return 'text-yellow-800 font-medium';
  return 'text-gray-700';
}
